import Image from "@Components/Image";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import getImagePath from "@Utils/getImagePath";
import { memo } from "react";
import {
  getFeaturedImage,
  getFeaturedImageAtFirst,
  getFormattedGalleryAssets,
  getFormattedTags,
  getPricingPriceFormatted,
  isAvailable,
  getMinimumAvailableFromValue,
  chinaFormattedDate,
} from "../../utils";
import styles from "./RoomTypeSection.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useRoomTypeContext from "../../context/useRoomTypeContext";
import ThreeDIcon from "@Icons/ThreeDIcon";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import TagV2 from "./components/Tag/TagV2";
import PhotosIcon from "@Icons/PhotosIcon";
import Shimmer from "@Components/Shimmer/Shimmer";
import Divider from "@Components/Divider";
import { useDispatch } from "react-redux";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import useWhitelabel from "hooks/useWhitelabel";
import config from "@Config/index";
import useIsLanguage from "hooks/useIsLanguage";
import { getTranslatedDurationValue } from "./components/TenancyCard/utils";
import replaceAmberDomainInLinks from "@Utils/clientUtils/dangerouslySetInnerHtmlUtility/replaceAmberDomainInLinks";

type RoomTypeCardProps = {
  id?: string;
  data?: any;
  isDesktop?: boolean;
};

type AmenityTag = {
  text: string;
  icon: string;
};

const RoomTypeCardV2WithSsrData = ({ id, data, isDesktop = false }: RoomTypeCardProps) => {
  const dispatch = useDispatch();
  const { propertyData } = useRoomTypeContext();
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
  const { t } = useTranslationHook();
  const language = useIsLanguage();
  const { isWhitelabel } = useWhitelabel();
  const isRootInventoryPricePerPersonEnabled = data?.pricing?.price_per_person || false;
  const isSharedRoom = data?.meta?.types?.includes("shared_room");
  const price = getPricingPriceFormatted(
    { ...data.pricing, price_per_person: isSharedRoom && isRootInventoryPricePerPersonEnabled },
    { price_per_person: true },
  );
  const available = isAvailable(data);
  const shouldShow3D = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.ROOM_TYPE_3D);

  const minAvailableFrom = getMinimumAvailableFromValue(
    available,
    data?.meta,
    data?.active_children_with_filters || [],
  );
  const minAvailableFromChinaDate = chinaFormattedDate(minAvailableFrom);
  const isFlexible =
    (data?.active_children_with_filters || []).length === 0 ||
    (data?.active_children_with_filters || []).some(
      (item: any) => item.available && !item?.meta?.available_from,
    );

  // Exclude sold-out rooms from SSR
  if (!available) {
    return null;
  }

  const featuredImage =
    data?.images.length > 0
      ? getFeaturedImage(data?.images)
      : getFeaturedImage(propertyData?.images);
  const tags: AmenityTag[] = getFormattedTags(data, t) || [];

  const featuredImageAsset = [
    {
      ...featuredImage,
      configName: data.name,
      category: "image",
    },
  ];
  const roomAssets = getFormattedGalleryAssets(data);
  const translatedPrice = getTranslatedDurationValue(price, t, "/");

  const roomName = config.IS_CHINA_APP && data.name_zh ? data.name_zh : t(data.name) || "";
  const rawDescription = data?.description?.[0]?.value || "";
  // Only show description during SSR when language is English, since children_short
  const roomDescription = language === "en" || !rawDescription ? rawDescription : "";

  const images = data?.images?.length ? getFeaturedImageAtFirst(data?.images) : [featuredImage];
  const virtualView = data?.virtual_views.map((item: any) => ({
    ...item,
    category: "3D",
    caption: "3D",
    configName: data?.name,
  }));

  const handleFeaturedImageClick = (index: any, is3D = false) => {
    gtagEventEmitter({
      action: "room-type-image-clicked",
      category: "room-type",
      label: is3D ? "3D" : "image",
    });
    propertyPageDispatcher.showGalleryCarousel({
      show: true,
      images: isDesktop || !shouldShow3D ? [...images] : [...virtualView, ...images],
      category: "room-type",
      currentItemIndex: index,
      showViewRoomButton: false,
      config_id: data.id,
    });
  };

  const amenityTags: any = [
    { name: "Room Details", values: [] },
    ...(Array.isArray(data?.features) ? data.features : []),
  ];
  tags?.forEach((tag: any) => {
    amenityTags[0].values.push({
      ...tag,
      name: tag.text,
    });
  });
  const avbFromText = t("propertypage.roomtypesectiondynamic.roomtypecard.availablefrom");
  const is3DPresent = virtualView?.length > 0;

  return (
    <div
      className={formatClassNames(styles.parentContainer, isDesktop && styles.desktop)}
      id={id}
      data-testid="room-types-card"
    >
      <div className={styles.infoSection}>
        <div
          className={formatClassNames(styles.herov2, isDesktop && styles.desktop, styles.herov2SSR)}
        >
          {isDesktop ? (
            <div
              className={styles.imageContainer}
              onClick={() => handleFeaturedImageClick(0)}
              role="button"
              tabIndex={0}
              onKeyDown={() => handleFeaturedImageClick(0)}
            >
              <Image
                dataTestId="propety-room-type-image"
                src={getImagePath(featuredImage?.path, "small")}
                className={styles.image}
                height={140}
                width={140}
                alt="Room type image"
              />
              <div className={styles.smallIconTagsContainer}>
                <div className={styles.imageCount}>
                  <PhotosIcon width="12px" height="12px" fill="white" />
                  {Number(images.length)}
                </div>
                {is3DPresent && (
                  <div className={formatClassNames(styles.imageCount)}>
                    <ThreeDIcon width={14} height={14} />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {!isDesktop && (
                <div className={styles.titleV2MobileContainer}>
                  <h3
                    data-testid="mobile-propety-room-type-name"
                    className={formatClassNames(
                      styles.titleV2,
                      isWhitelabel ? "wl-title-font" : "",
                    )}
                  >
                    {roomName}
                  </h3>
                  {roomDescription && (
                    <div
                      className={styles.roomDescriptionV2SSR}
                      dangerouslySetInnerHTML={{
                        __html: replaceAmberDomainInLinks(roomDescription),
                      }}
                    />
                  )}
                </div>
              )}
              <div className={styles.horizontalScroll}>
                {shouldShow3D &&
                  virtualView?.map((item: any, index: any) => (
                    <div key={`imgs${index}`} className={styles.imageMobileContainer} role="none">
                      <Image
                        dataTestId="mobile-property-room-type-images"
                        src={getImagePath(images[index]?.path, "room-type-card")}
                        className={styles.imageMobile}
                        height={122}
                        width={211}
                        alt={item?.type}
                        addShimmer
                      />
                      <div className={styles.overlay}>
                        <ThreeDIcon />
                      </div>
                    </div>
                  ))}
                {images.map((img: any, idx: any) => (
                  <div key={`imgs${idx}`} className={styles.imageContainerV2}>
                    <Image
                      dataTestId="mobile-property-room-type-images"
                      src={getImagePath(img?.path, "room-type-card")}
                      className={styles.imageMobile}
                      height={122}
                      width={211}
                      alt="Room type image"
                      addShimmer
                    />
                    {idx === 0 && (
                      <div className={styles.imageCount}>
                        <PhotosIcon width="12px" height="12px" fill="white" />
                        {Number(roomAssets.length || featuredImageAsset.length)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
          <div className={styles.info}>
            {isDesktop && (
              <>
                <h3
                  data-testid="propety-room-type-name"
                  className={formatClassNames(styles.titleV2, isDesktop && styles.desktop)}
                >
                  {roomName}
                </h3>
                {roomDescription && (
                  <div
                    className={styles.roomDescriptionV2SSR}
                    dangerouslySetInnerHTML={{ __html: replaceAmberDomainInLinks(roomDescription) }}
                  />
                )}
              </>
            )}
            <div
              className={formatClassNames(
                styles.descriptionv2,
                isDesktop && styles.desktop,
                !available && styles.row,
              )}
            >
              {minAvailableFrom && isDesktop && !isFlexible && (
                <span
                  data-testid="property-room-type-availability-from"
                  className={styles.availabilityContainer}
                >
                  {avbFromText}:{" "}
                  <span>{config.IS_CHINA_APP ? minAvailableFromChinaDate : minAvailableFrom}</span>
                </span>
              )}
              {isFlexible && available && isDesktop && (
                <span
                  data-testid="property-room-type-availability-from"
                  className={styles.availabilityContainer}
                >
                  {t("propertypage.roomtypesectiondynamic.roomtypecard.flexiblemoveindates")}
                </span>
              )}
              {minAvailableFrom && !isDesktop && !isFlexible && (
                <span
                  data-testid="property-room-type-availability-from"
                  className={styles.availabilityContainerMobile}
                >
                  {avbFromText} :{" "}
                  <span>{config.IS_CHINA_APP ? minAvailableFromChinaDate : minAvailableFrom}</span>
                </span>
              )}
              {isFlexible && available && !isDesktop && (
                <span
                  data-testid="property-room-type-availability-from"
                  className={styles.availabilityContainerMobile}
                >
                  {t("propertypage.roomtypesectiondynamic.roomtypecard.flexiblemoveindates")}
                </span>
              )}
              {!isDesktop && available && <span className={styles.dot}>·</span>}
              {(isDesktop || !available) && <span className={styles.dot}>·</span>}
              <span data-testid="property-room-type-price-range" className={styles.priceContainer}>
                {t("From")}: <span>{language !== "en" ? translatedPrice : price}</span>
              </span>
            </div>

            {isDesktop && (
              <div
                className={formatClassNames(
                  styles.amenitiesContainerV2,
                  isDesktop && styles.desktop,
                )}
              >
                {tags.slice(0, 6).map((item) => (
                  <TagV2 key={item?.text} name={item?.text} icon={item?.icon} />
                ))}
              </div>
            )}
            {isDesktop && (
              <div
                className={formatClassNames(styles.viewMoreContainer, isDesktop && styles.desktop)}
              >
                <Shimmer width="500" noMargin height="20" />
              </div>
            )}
          </div>
        </div>
        {!isDesktop && (
          <div className={styles.amenities}>
            {tags.slice(0, 6).map((item) => (
              <TagV2 key={item?.text} name={item?.text} icon={item?.icon} />
            ))}
          </div>
        )}
        {!isDesktop && (
          <div className={formatClassNames(styles.viewMoreContainer, isDesktop && styles.desktop)}>
            <Shimmer width="250" noMargin height="20" />
          </div>
        )}
        <Divider size="xs" color="#e5e7eb" />
        <div className={styles.tenanciesShimmerContainer}>
          <div>
            <Shimmer addShadow noMargin width={isDesktop ? "250" : "150"} height="25" />
            <Shimmer addShadow noMargin width={isDesktop ? "250" : "150"} height="25" />
          </div>
          <div>
            <Shimmer addShadow noMargin width={isDesktop ? "250" : "150"} height="30" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default wrapperHOC(memo(RoomTypeCardV2WithSsrData), {
  componentName: "RoomTypeCardV2-RoomTypeSection-PropertyPage",
  showForChina: true,
});
